import { ModalElement } from '@/components/custom-elements/modal'

type BrowsingContextSuggestionsResponse = {
  detected_values: {
    country_name: string
    country: {
      handle: string
      name: string
    }
  }
  features: Record<string, unknown>
  suggestions: []
}

type SuggestionCountry = {
  code: string
  name: string
  currency: string
  flag: string
  url: string
}

class LocalizationForm extends HTMLElement {
  elements: {
    input: HTMLInputElement | null
    search: HTMLInputElement | null
    list: HTMLElement | null
    modal: ModalElement | null
    suggestionModal: ModalElement | null
    suggestionData: HTMLScriptElement | null
    suggestionFlag: HTMLImageElement | null
    suggestionHeading: HTMLElement | null
    suggestionText: HTMLElement | null
    suggestionSwitchLink: HTMLAnchorElement | null
  }

  selectors = {
    name: 'data-name',
    value: 'data-value'
  }

  constructor() {
    super()
    this.elements = this.queryElements()
  }

  connectedCallback() {
    this.fetchElements()
    this.addEventListeners()
    // TODO: revisit NZ specific localization - auto-detect suggestion modal disabled for now, only the manual currency selector is in use.
    // this.getCurrentCountry()
  }

  queryElements() {
    return {
      input: this.querySelector<HTMLInputElement>('input[name="language_code"], input[name="country_code"]'),
      search: this.querySelector<HTMLInputElement>('#CountrySearch'),
      list: this.querySelector<HTMLElement>('#CountryList'),
      modal: document.querySelector<ModalElement>('[data-id="localization"]'),
      suggestionModal: document.querySelector<ModalElement>('[data-id="localization-suggestion"]'),
      suggestionData: document.querySelector<HTMLScriptElement>('#localization-suggestion-data'),
      suggestionFlag: document.querySelector<HTMLImageElement>('#localization-suggestion-flag'),
      suggestionHeading: document.querySelector<HTMLElement>('#localization-suggestion-heading'),
      suggestionText: document.querySelector<HTMLElement>('#localization-suggestion-text'),
      suggestionSwitchLink: document.querySelector<HTMLAnchorElement>('#localization-suggestion-switch')
    }
  }

  fetchElements() {
    this.elements = this.queryElements()
  }

  addEventListeners() {
    this.elements.search?.addEventListener('input', this.onSearchInput.bind(this))
    this.querySelectorAll('a[data-value]').forEach((item) =>
      item.addEventListener('click', this.onItemClick.bind(this))
    )
  }

  onSearchInput(event: Event) {
    event.preventDefault()
    const currentTarget = event.currentTarget as HTMLInputElement
    const value = currentTarget?.value?.toLowerCase()
    this.elements?.list?.querySelectorAll('a').forEach((item) => {
      const name = item.getAttribute(this.selectors.name)?.toLowerCase()
      const itemValue = item.getAttribute(this.selectors.value)?.toLowerCase()
      const wrappingListItem = item.closest('li')
      if (!wrappingListItem) return
      if (name?.includes(value) || itemValue?.includes(value)) wrappingListItem.style.display = 'block'
      else wrappingListItem.style.display = 'none'
    })
  }

  onItemClick(event: Event) {
    event.preventDefault()
    const form = this.querySelector('form')
    const target = event.target as HTMLElement
    const dataElement = target.closest('[data-value]') as HTMLElement | null

    if (!this.elements.input || !dataElement?.dataset.value) return
    this.elements.input.value = dataElement.dataset.value
    if (form) form.submit()
  }

  getSuggestionCountries(): SuggestionCountry[] {
    if (!this.elements.suggestionData?.textContent) return []
    try {
      return JSON.parse(this.elements.suggestionData.textContent)
    } catch {
      return []
    }
  }

  async getCurrentCountry() {
    if (!window.Shopify) return
    if (sessionStorage.getItem('localization-modal-shown')) return

    const response: BrowsingContextSuggestionsResponse = await fetch(
      window.Shopify.routes.root + 'browsing_context_suggestions.json'
    ).then((r) => r.json())
    const currentCountry = window.Shopify.country
    const countryHandle = response.detected_values.country.handle
    const countries = this.getSuggestionCountries()
    const matchingCountry = countries.find((country) => country.code?.toLowerCase() === countryHandle.toLowerCase())

    if (!matchingCountry?.url) return
    if (matchingCountry.code.toLowerCase() === currentCountry?.toLowerCase()) return

    this.showSuggestionModal(matchingCountry)
    sessionStorage.setItem('localization-modal-shown', 'true')
  }

  showSuggestionModal(country: SuggestionCountry) {
    const { suggestionModal, suggestionFlag, suggestionHeading, suggestionText, suggestionSwitchLink } = this.elements
    if (!suggestionModal) return

    if (suggestionFlag) {
      suggestionFlag.src = country.flag
      suggestionFlag.alt = country.name
    }
    if (suggestionHeading) {
      suggestionHeading.textContent = (suggestionHeading.dataset.template || '').replace(/%%COUNTRY%%/g, country.name)
    }
    if (suggestionText) {
      suggestionText.textContent = (suggestionText.dataset.template || '')
        .replace(/%%COUNTRY%%/g, country.name)
        .replace(/%%CURRENCY%%/g, country.currency)
    }
    if (suggestionSwitchLink) {
      suggestionSwitchLink.textContent = (suggestionSwitchLink.dataset.template || '').replace(
        /%%COUNTRY%%/g,
        country.name
      )
      suggestionSwitchLink.href = country.url
    }

    suggestionModal.handleShow()
  }
}

customElements.define('localization-form', LocalizationForm)
